import api from "../components/services/api";

// Module-level cache — survives across the whole session, shared by every
// component that imports this file. Keyed by employee id. Storing the full
// employee record here means any component (Leaves list, ViewLeave popup,
// etc.) can show department/designation instantly once an employee has been
// fetched once, instead of each component re-fetching independently.
export const employeeDetailCache = new Map();

// Dedupes concurrent requests for the same employee — if two components ask
// for the same id at the same time (e.g. prefetch + a click that opens the
// modal a moment later), only one network call goes out; both callers get
// the same promise and resolve together once it lands.
const inFlight = new Map();

/**
 * Fetch (and cache) an employee's full record. Safe to call many times for
 * the same id — subsequent calls resolve instantly from the cache, and
 * concurrent calls share one in-flight request.
 */
export const fetchEmployeeFull = (empId) => {
  if (!empId) return Promise.resolve(null);

  const cached = employeeDetailCache.get(empId);
  if (cached) return Promise.resolve(cached);

  const pending = inFlight.get(empId);
  if (pending) return pending;

  const request = api
    .get(`employees/${empId}/`)
    .then((res) => {
      const full = res.data?.data || res.data || res;
      employeeDetailCache.set(empId, full);
      return full;
    })
    .catch(() => null)
    .finally(() => {
      inFlight.delete(empId);
    });

  inFlight.set(empId, request);
  return request;
};

/**
 * Prefetch several employees at once (fire-and-forget). Used by the Leaves
 * list to warm the cache for every employee currently on screen, so opening
 * any of their ViewLeave popups feels instant instead of waiting on a fetch.
 * Already-cached or already-in-flight ids are skipped automatically by
 * fetchEmployeeFull, so calling this repeatedly (e.g. on every page change)
 * is cheap.
 */
export const prefetchEmployees = (empIds = []) => {
  const uniqueIds = [...new Set(empIds.filter(Boolean))];
  uniqueIds.forEach((id) => {
    fetchEmployeeFull(id);
  });
};